import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A change the assistant proposed through a tool (Projects AI). The server stores it; the chat only shows
 * it and records what the person did, so the next turn can tell the model (see `serialise`).
 */
export interface AssistantProposal {
  id: string;
  projectId: number;
  kind: string;
  summary: { title: string; reason: string | null; lines: { label: string; before?: string | null; after: string }[] };
  status: string;
  result: unknown;
  createdAt: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  proposals?: AssistantProposal[];
}

export interface AssistantChatState {
  messages: ChatMessage[];
  input: string;
  setInput: (v: string) => void;
  streaming: boolean;
  /** What the assistant is doing right now (e.g. "Autotask: search tickets…"); agent chat only. */
  status: string | null;
  error: string | null;
  send: (text: string) => void;
  /** Start a new conversation. */
  reset: () => void;
  /** Record what happened to a proposal (applied / discarded / failed) after Apply or Discard. */
  updateProposal: (p: AssistantProposal) => void;
}

/** What the server sees: plain text, with one line per proposal so the model knows what was applied. */
function serialise(m: ChatMessage): { role: ChatMessage['role']; content: string } {
  if (!m.proposals?.length) return { role: m.role, content: m.content };
  const notes = m.proposals.map((p) => `[Cosmos: proposal ${p.id.slice(0, 8)} "${p.summary.title}" is ${p.status === 'pending' ? 'still waiting (not applied)' : p.status}]`);
  return { role: m.role, content: [m.content.trim(), ...notes].filter(Boolean).join('\n') };
}

function loadMessages(key: string | undefined): ChatMessage[] {
  if (!key) return [];
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((m) => (m?.role === 'user' || m?.role === 'assistant') && typeof m.content === 'string' && m.content) : [];
  } catch { return []; }
}

/**
 * Shared SSE chat driver for the AI assistants (ticket + sales).
 *
 * Posts `{ ...body(), messages }` to `endpoint` and appends the streamed `{delta}` frames to
 * the trailing assistant message. All entity context is assembled server-side — `body()` only
 * supplies the scope (which ticket / which opportunity / which pipeline), never the data.
 *
 * `resetKey` clears the conversation when the subject changes (e.g. navigating to another
 * ticket or opportunity), so replies never carry over from a different record.
 *
 * `storageKey` keeps the conversation in sessionStorage, so it survives navigating away and back
 * (the /ai chat — a record link in a reply leaves the page). `{status}` frames set `status`.
 */
export function useAssistantStream(opts: {
  endpoint: string;
  body: () => Record<string, unknown>;
  resetKey?: unknown;
  storageKey?: string;
  /** A custom SSE frame (e.g. the quote AI's `{ quoteDocument }`); return true when handled. */
  onFrame?: (frame: Record<string, unknown>) => boolean;
  /** After each reply finishes (or fails). */
  onSettled?: () => void;
}): AssistantChatState {
  const { endpoint, resetKey, storageKey } = opts;
  const onFrameRef = useRef(opts.onFrame);
  onFrameRef.current = opts.onFrame;
  const onSettledRef = useRef(opts.onSettled);
  onSettledRef.current = opts.onSettled;
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessages(storageKey));
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `body` is typically an inline arrow, so it changes identity every render — hold the latest
  // in a ref rather than making it a dependency of `send` (which would re-create the callback
  // on every render and defeat the memoisation).
  const bodyRef = useRef(opts.body);
  bodyRef.current = opts.body;

  // Mirrors of the live state so `send` can read them without listing them as deps.
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;
  const streamingRef = useRef(false);

  const reset = useCallback(() => {
    setMessages([]);
    setInput('');
    setError(null);
    if (storageKey) try { sessionStorage.removeItem(storageKey); } catch { /* storage unavailable */ }
  }, [storageKey]);

  // Clear only when the key actually changes (not on mount, which would drop a restored chat —
  // StrictMode re-runs mount effects, so compare against the last key rather than a "first run" flag).
  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (Object.is(lastResetKey.current, resetKey)) return;
    lastResetKey.current = resetKey;
    reset();
  }, [resetKey, reset]);

  // Persist settled conversations (not every streamed delta).
  useEffect(() => {
    if (!storageKey || streaming) return;
    try {
      if (messages.length) sessionStorage.setItem(storageKey, JSON.stringify(messages.filter((m) => m.content || m.proposals?.length)));
      else sessionStorage.removeItem(storageKey);
    } catch { /* storage unavailable or full */ }
  }, [messages, streaming, storageKey]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || streamingRef.current) return;
    setError(null);
    // Drop empty assistant turns (a reply that errored before any text) — the API rejects them.
    const history: ChatMessage[] = [...messagesRef.current.filter((m) => m.content || m.proposals?.length), { role: 'user', content: trimmed }];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setInput('');
    streamingRef.current = true;
    setStreaming(true);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bodyRef.current(), messages: history.map(serialise) }),
      });
      if (!res.ok || !res.body) {
        let msg = `Request failed (${res.status})`;
        try { msg = (await res.json())?.error?.message ?? msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') continue;
          try {
            const obj = JSON.parse(data) as { delta?: string; status?: string; error?: string; proposal?: AssistantProposal };
            if (onFrameRef.current?.(obj as Record<string, unknown>)) {
              // handled by the page
            } else if (obj.error) {
              setError(obj.error);
            } else if (obj.proposal) {
              const proposal = obj.proposal;
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, proposals: [...(last.proposals ?? []), proposal] };
                return copy;
              });
            } else if (obj.status) {
              setStatus(obj.status);
            } else if (obj.delta) {
              setStatus(null);
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, content: last.content + obj.delta };
                return copy;
              });
            }
          } catch { /* ignore malformed chunk */ }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      // Drop the empty assistant placeholder so the thread doesn't show a blank bubble.
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === '' && !last.proposals?.length) return prev.slice(0, -1);
        return prev;
      });
    } finally {
      streamingRef.current = false;
      setStreaming(false);
      setStatus(null);
      onSettledRef.current?.();
    }
  }, [endpoint]);

  const updateProposal = useCallback((p: AssistantProposal) => {
    setMessages((prev) => prev.map((m) => (m.proposals?.some((x) => x.id === p.id) ? { ...m, proposals: m.proposals.map((x) => (x.id === p.id ? p : x)) } : m)));
  }, []);

  return { messages, input, setInput, streaming, status, error, send, reset, updateProposal };
}
